import type { ReactNode } from "react";
import {
  Image,
  Pressable,
  Text,
  View,
  type ImageSourcePropType,
  type ImageStyle,
  type StyleProp,
} from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { Tag, cardSurface } from "@louise/moon-ui";
import { EMBER } from "../../../onboarding/theme";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

type AccessGridCardProps = {
  title: string;
  description?: string;
  imageSource?: ImageSourcePropType;
  imageStyle?: StyleProp<ImageStyle>;
  fallbackVisual?: ReactNode;
  tags?: ReactNode;
  onPress: () => void;
  /** Stable analytics identifier for the grid this card belongs to. */
  action?: string;
  /**
   * `glass` on a page; `panel` when the grid sits inside a card (a fact
   * sheet's testing tab), so the tiles do not draw a card inside a card.
   */
  surface?: "glass" | "panel";
};

export function AccessGridCardPill({
  label,
  icon,
}: {
  label: string;
  icon?: LucideIcon;
}) {
  // Compact: at the default size, labels such as "Same day (a few hours)" run
  // past a two-column card.
  return <Tag label={label} icon={icon} variant="outline" size="compact" />;
}

export default function AccessGridCard({
  title,
  description,
  imageSource,
  imageStyle,
  fallbackVisual,
  tags,
  onPress,
  action = "access_grid_card",
  surface = "glass",
}: AccessGridCardProps) {
  const capturePress = usePressCapture("AccessGridCard");
  return (
    <Pressable
      onPress={() => {
        // Card titles are authored catalogue copy, not patient data.
        capturePress(pressLabel(title), action);
        onPress();
      }}
      style={{
        ...cardSurface({ variant: surface }),
        flex: 1,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          alignItems: "center",
          paddingTop: 24,
          paddingBottom: 12,
        }}
      >
        {imageSource ? (
          <Image source={imageSource} style={imageStyle} resizeMode="contain" />
        ) : (
          fallbackVisual
        )}
      </View>

      <View
        style={{
          flex: 1,
          padding: 16,
          paddingTop: 4,
          gap: 6,
          alignItems: "center",
        }}
      >
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            fontWeight: "700",
            color: EMBER.colors.text,
            lineHeight: 20,
            textAlign: "center",
          }}
          numberOfLines={2}
        >
          {title}
        </Text>

        {description ? (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 12,
              color: EMBER.colors.textLight,
              lineHeight: 17,
              textAlign: "center",
            }}
            numberOfLines={2}
          >
            {description}
          </Text>
        ) : null}

        <View style={{ flex: 1 }} />

        {tags ? (
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 4,
              marginTop: 4,
            }}
          >
            {tags}
          </View>
        ) : null}
      </View>
    </Pressable>
  );
}
